import type { FeatureName } from "@lib/featureFlipper";

import {
  messagePrioritizationDefaultCategoriesLimit,
  messagePrioritizationDefaultRetryWindowDays,
  messagePrioritizationDefaultRulesLimit,
  messagePrioritizationDefaultScheduledActiveOptInLimit,
  messagePrioritizationDefaultTriggeredActiveOptInLimit,
} from "./defaultLimits";
import { isMessagePrioritizationEnabled } from "./isMessagePrioritizationEnabled";
import {
  messagePrioritizationPaidCategoriesLimit,
  messagePrioritizationPaidRetryWindowDays,
  messagePrioritizationPaidRulesLimit,
  messagePrioritizationPaidScheduledActiveOptInLimit,
  messagePrioritizationPaidTriggeredActiveOptInLimit,
} from "./paidLimits";
import type { MessagePrioritizationFallbackLimits } from "./types";

const standardFallbackLimits: MessagePrioritizationFallbackLimits = {
  scheduledActiveOptInLimit: messagePrioritizationDefaultScheduledActiveOptInLimit,
  triggeredActiveOptInLimit: messagePrioritizationDefaultTriggeredActiveOptInLimit,
  categoriesLimit: messagePrioritizationDefaultCategoriesLimit,
  rulesLimit: messagePrioritizationDefaultRulesLimit,
  retryWindowDays: messagePrioritizationDefaultRetryWindowDays,
};

const paidFallbackLimits: MessagePrioritizationFallbackLimits = {
  scheduledActiveOptInLimit: messagePrioritizationPaidScheduledActiveOptInLimit,
  triggeredActiveOptInLimit: messagePrioritizationPaidTriggeredActiveOptInLimit,
  categoriesLimit: messagePrioritizationPaidCategoriesLimit,
  rulesLimit: messagePrioritizationPaidRulesLimit,
  retryWindowDays: messagePrioritizationPaidRetryWindowDays,
};

/**
 * The limits to assume for a workspace when the feature-limits query has not answered (loading, failed,
 * or the key is absent from the response). Mirrors the backend's `feature_on_default` rule for these
 * keys: the paid numbers apply only when `message_prioritization_paid` is on together with
 * `message_prioritization_m1` or `message_prioritization`. Company-level overrides cannot be known
 * here, so the tier value is a floor, never above what the company actually has.
 *
 * @param featureOn - the checker returned by `useFeatureOn`
 * @returns the fallback set for the workspace's tier
 */
export const getMessagePrioritizationFallbackLimits = (
  featureOn: (featureName: FeatureName) => boolean
): MessagePrioritizationFallbackLimits => {
  if (featureOn("message_prioritization_paid") && isMessagePrioritizationEnabled(featureOn)) {
    return paidFallbackLimits;
  }
  return standardFallbackLimits;
};
